import { LoggerService } from '@angular-ru/cdk/logger';
import { ChangeDetectionStrategy, Component, effect, inject, input } from '@angular/core';
import { FaIconComponent as FaIconLibraryComponent } from '@fortawesome/angular-fontawesome';
import { IFaIcon } from 'src/app/api/modules/icons/fa/IFaIcon';
import { FaIconNamePipe } from '../../pipes/fa-icon-name.pipe';

/**
 * Renders a FontAwesome icon from a server-provided {@link IFaIcon} config.
 *
 * ### CSS Custom Properties
 *
 * Set these from any ancestor to restyle the icon — they inherit through
 * view encapsulation, so no `::ng-deep` or `:host-context` is needed.
 *
 * | Property | Description | Default |
 * | --- | --- | --- |
 * | `--ds-fa-icon-margin` | Spacing around the icon | `0` and half the design-system spacer |
 * | `--ds-fa-icon-text-align` | Alignment within the host | `center` |
 * | `--ds-fa-icon-color` | Color when the config doesn't set one | `inherit` |
 * | `--ds-fa-icon-font-size` | Size when the config doesn't set one | `inherit` |
 */
@Component({
  selector: 'app-fa-icon',
  templateUrl: './fa-icon.component.html',
  styleUrls: ['./fa-icon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FaIconLibraryComponent, FaIconNamePipe],
})
export class FaIconComponent {
  private readonly loggerService = inject(LoggerService);

  /**
   * The Config as provided to the element.
   *
   * Nothing renders until a truthy config arrives, so observable-driven
   * consumers can safely bind `someIcon$ | async`.
   */
  readonly config = input<IFaIcon | null>();

  constructor() {
    // Migration aid: M2 palette names are not CSS colors and silently fail in M3.
    effect(() => {
      const color = this.config()?.color;
      if (color === 'primary' || color === 'accent' || color === 'warn') {
        this.loggerService.warn(
          '[FaIcon] The "color" property of an icon should no longer be "primary" "accent" or "warn". These do not apply to Material 3.',
        );
      }
    });
  }
}
